// Utility functions for filtering flight search results

/**
 * Extract available filter options from search results
 */
export const extractAvailableFilters = (searchResults) => {
  if (!searchResults || searchResults.length === 0) {
    return {
      airlines: [],
      stops: [],
      cabins: [],
      amenities: [],
      layoverAirports: [],
      priceRange: { min: 0, max: 0 },
      stopsPrices: {}
    };
  }

  const airlines = new Set();
  const stops = new Set();
  const cabins = new Set();
  const amenities = new Set();
  const layoverAirports = new Set();
  const prices = [];
  const stopsPrices = { 0: [], 1: [], 2: [] };

  searchResults.forEach(result => {
    // Extract from outbound flight
    if (result.outbound) {
      airlines.add(result.outbound.airline);
      stops.add(result.outbound.stops);
      cabins.add(result.outbound.cabinClass);
      if (result.outbound.amenities) {
        result.outbound.amenities.forEach(amenity => amenities.add(amenity));
      }
      if (result.outbound.layover && result.outbound.layover.airport) {
        layoverAirports.add(result.outbound.layover.airport);
      }
      if (result.outbound.price && result.outbound.price.economy) {
        const price = result.outbound.price.economy;
        prices.push(price);
        // Group prices by stops
        if (result.outbound.stops <= 2) {
          stopsPrices[result.outbound.stops].push(price);
        } else {
          stopsPrices[2].push(price); // 2+ stops
        }
      }
    }

    // Extract from return flights
    if (result.returnFlights) {
      result.returnFlights.forEach(returnFlight => {
        airlines.add(returnFlight.airline);
        stops.add(returnFlight.stops);
        cabins.add(returnFlight.cabinClass);
        if (returnFlight.amenities) {
          returnFlight.amenities.forEach(amenity => amenities.add(amenity));
        }
        if (returnFlight.layover && returnFlight.layover.airport) {
          layoverAirports.add(returnFlight.layover.airport);
        }
        if (returnFlight.price && returnFlight.price.economy) {
          const price = returnFlight.price.economy;
          prices.push(price);
          // Group prices by stops
          if (returnFlight.stops <= 2) {
            stopsPrices[returnFlight.stops].push(price);
          } else {
            stopsPrices[2].push(price); // 2+ stops
          }
        }
      });
    }

    // Add total price
    if (result.totalPrice) {
      prices.push(result.totalPrice);
    }
  });

  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

  // Calculate minimum prices for each stop type
  const stopsPricesWithMin = {};
  Object.keys(stopsPrices).forEach(stopType => {
    const stopPrices = stopsPrices[stopType];
    stopsPricesWithMin[stopType] = stopPrices.length > 0 ? Math.min(...stopPrices) : null;
  });

  return {
    airlines: Array.from(airlines).sort(),
    stops: Array.from(stops).sort(),
    cabins: Array.from(cabins).sort(),
    amenities: Array.from(amenities).sort(),
    layoverAirports: Array.from(layoverAirports).sort(),
    priceRange: { min: minPrice, max: maxPrice },
    stopsPrices: stopsPricesWithMin
  };
};

/**
 * Check if a flight matches the stops filter
 */
const matchesStopsFilter = (flight, stopsFilter) => {
  if (!stopsFilter || stopsFilter.length === 0) return true;
  
  const stops = flight.stops;
  return stopsFilter.some(filter => {
    switch (filter) {
      case 'nonstop':
        return stops === 0;
      case '1stop':
        return stops === 1;
      case '2stops':
        return stops >= 2;
      default:
        return false;
    }
  });
};

/**
 * Check if a flight matches the airlines filter
 */
const matchesAirlinesFilter = (flight, airlinesFilter) => {
  if (!airlinesFilter || airlinesFilter.length === 0) return true;
  return airlinesFilter.includes(flight.airline);
};

/**
 * Check if a flight matches the cabin filter
 */
const matchesCabinFilter = (flight, cabinFilter) => {
  if (!cabinFilter || cabinFilter.length === 0) return true;
  return cabinFilter.includes(flight.cabinClass);
};

/**
 * Check if a flight matches the amenities filter
 */
const matchesAmenitiesFilter = (flight, amenitiesFilter) => {
  if (!amenitiesFilter || amenitiesFilter.length === 0) return true;
  if (!flight.amenities) return false;
  
  return amenitiesFilter.every(amenity => flight.amenities.includes(amenity));
};

/**
 * Check if a flight matches the layover airports filter
 */
const matchesLayoverAirportsFilter = (flight, layoverAirportsFilter) => {
  if (!layoverAirportsFilter || layoverAirportsFilter.length === 0) return true;
  if (!flight.layover || !flight.layover.airport) return true; // Direct flights pass through
  
  return layoverAirportsFilter.includes(flight.layover.airport);
};

/**
 * Check if a price is within the price range
 */
const matchesPriceFilter = (price, priceRange) => {
  if (!priceRange) return true;
  return price >= priceRange.min && price <= priceRange.max;
};

/**
 * Convert time string (HH:MM) to decimal hours
 */
const timeToDecimal = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + minutes / 60;
};

/**
 * Convert duration string to minutes
 */
const durationStringToMinutes = (durationString) => {
  const match = durationString.match(/(\d+)h\s*(\d+)?m?/);
  if (match) {
    const hours = parseInt(match[1]);
    const minutes = match[2] ? parseInt(match[2]) : 0;
    return hours * 60 + minutes;
  }
  return 0;
};

/**
 * Check if a flight time matches the time range filter
 */
const matchesTimeFilter = (flight, timeRanges, timeFilter) => {
  if (!timeRanges || Object.keys(timeRanges).length === 0) return true;
  
  const timeField = timeFilter === 'takeoff' ? 'departure' : 'arrival';
  const flightTime = timeToDecimal(flight[timeField].time);
  
  // Check if flight time falls within any of the time ranges
  return Object.values(timeRanges).some(range => {
    return flightTime >= range.start && flightTime <= range.end;
  });
};

/**
 * Check if a flight duration matches the duration range filter
 */
const matchesDurationFilter = (flight, durationRanges, durationType) => {
  if (!durationRanges || !durationRanges[durationType]) return true;
  
  const flightDuration = durationStringToMinutes(flight.duration);
  const range = durationRanges[durationType];
  
  // When using a single-handle max UI we may set start to 0. Treat undefined start as 0.
  const start = typeof range.start === 'number' ? range.start : 0;
  const end = typeof range.end === 'number' ? range.end : Number.MAX_SAFE_INTEGER;
  return flightDuration >= start && flightDuration <= end;
};

/**
 * Check if a flight's layover duration matches the layover range filter
 */
const matchesLayoverFilter = (flight, durationRanges) => {
  if (!durationRanges || !durationRanges.layover) return true;
  if (!flight.layover) return true; // Direct flights have no layover
  
  const layoverDuration = durationStringToMinutes(flight.layover.duration);
  const range = durationRanges.layover;
  
  return layoverDuration >= range.start && layoverDuration <= range.end;
};

/**
 * Apply all filters to search results
 */
export const applyFilters = (searchResults, filters) => {
  if (!searchResults || searchResults.length === 0) {
    return [];
  }

  const {
    stopsFilter = [],
    airlinesFilter = [],
    cabinFilter = [],
    amenitiesFilter = [],
    layoverAirportsFilter = [],
    priceRange = null,
    timeRanges = null,
    timeFilter = 'takeoff',
    durationRanges = null,
    flightQuality = { wifiOnly: false, redEyeOnly: false, layoverTransfersOnly: false },
  } = filters;

  return searchResults.filter(result => {
    // Check outbound flight
    const isRedEye = (flight) => {
      // red-eye: departure late night (>= 22:00) or arrival early morning (<= 06:00)
      const dep = timeToDecimal(flight.departure.time);
      const arr = timeToDecimal(flight.arrival.time);
      return dep >= 22 || arr <= 6;
    };

    const hasLayoverTransfer = (flight) => {
      return !!flight.layover; // dataset uses layover object when there is a transfer
    };

    const matchesFlightQuality = (flight) => {
      if (flightQuality?.wifiOnly && !(flight.amenities || []).includes('WiFi')) return false;
      if (flightQuality?.redEyeOnly && !isRedEye(flight)) return false;
      if (flightQuality?.layoverTransfersOnly && !hasLayoverTransfer(flight)) return false;
      return true;
    };

    const outboundMatches = 
      matchesStopsFilter(result.outbound, stopsFilter) &&
      matchesAirlinesFilter(result.outbound, airlinesFilter) &&
      matchesCabinFilter(result.outbound, cabinFilter) &&
      matchesAmenitiesFilter(result.outbound, amenitiesFilter) &&
      matchesLayoverAirportsFilter(result.outbound, layoverAirportsFilter) &&
      matchesTimeFilter(result.outbound, timeRanges, timeFilter) &&
      matchesDurationFilter(result.outbound, durationRanges, 'flightLeg') &&
      matchesLayoverFilter(result.outbound, durationRanges) &&
      matchesFlightQuality(result.outbound);

    if (!outboundMatches) return false;

    // Check return flights (if any)
    let returnMatches = true;
    if (result.returnFlights && result.returnFlights.length > 0) {
      returnMatches = result.returnFlights.some(returnFlight =>
        matchesStopsFilter(returnFlight, stopsFilter) &&
        matchesAirlinesFilter(returnFlight, airlinesFilter) &&
        matchesCabinFilter(returnFlight, cabinFilter) &&
        matchesAmenitiesFilter(returnFlight, amenitiesFilter) &&
        matchesLayoverAirportsFilter(returnFlight, layoverAirportsFilter) &&
        matchesTimeFilter(returnFlight, timeRanges, timeFilter) &&
        matchesDurationFilter(returnFlight, durationRanges, 'flightLeg') &&
        matchesLayoverFilter(returnFlight, durationRanges) &&
        matchesFlightQuality(returnFlight)
      );
    }

    if (!returnMatches) return false;

    // Check total price
    const priceMatches = matchesPriceFilter(result.totalPrice, priceRange);

    return priceMatches;
  });
};

/**
 * Get human-readable label for stops filter
 */
export const getStopsLabel = (stops) => {
  switch (stops) {
    case 0:
      return 'Nonstop';
    case 1:
      return '1 stop';
    default:
      return `${stops} stops`;
  }
};

/**
 * Convert stops number to filter key
 */
export const getStopsFilterKey = (stops) => {
  switch (stops) {
    case 0:
      return 'nonstop';
    case 1:
      return '1stop';
    default:
      return '2stops';
  }
};

/**
 * Convert decimal hours to time string (HH:MM AM/PM)
 */
export const decimalToTimeString = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  let displayHours = hours;
  let ampm = 'AM';
  
  if (hours >= 12) {
    ampm = 'PM';
    if (hours > 12) {
      displayHours = hours - 12;
    }
  }
  
  if (displayHours === 0) {
    displayHours = 12;
  }
  
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${formattedMinutes} ${ampm}`;
};

/**
 * Convert time string to decimal hours
 */
export const timeStringToDecimal = (timeString) => {
  const [time, ampm] = timeString.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  
  let decimalHours = hours + minutes / 60;
  
  if (ampm === 'PM' && hours !== 12) {
    decimalHours += 12;
  } else if (ampm === 'AM' && hours === 12) {
    decimalHours = minutes / 60;
  }
  
  return decimalHours;
};